const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * 60;

export const utcDateFormatTo12HrFormat = (utcFormatDate: string) => {
  const date = new Date(utcFormatDate);
  const formateddate = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })
    .format(date)
    .replace("at", "|");
  return formateddate;
};


/** Format date string to separate date and time display parts */
export const formatDate = (dateStr: string): { datePart: string; timePart: string } => {
    try {
        const date = new Date(dateStr);
        const options: Intl.DateTimeFormatOptions = {
            day: "2-digit",
            month: "long",
            year: "numeric",
            weekday: "long",
        };
        const datePart = date.toLocaleDateString("en-US", options);
        const timePart = date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
        return { datePart, timePart };
    } catch {
        return { datePart: dateStr, timePart: "" };
    }
};

/**
 * Compact date+time with timezone for space-constrained labels — e.g.
 * "Jul 11, 2026, 9:59 PM PDT". Uses short month so it fits on a single line
 * on narrow (phone) screens. Rendered in the viewer's local zone.
 */
export const formatDateTimeWithZone = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
};

/** Formats a date string to short date — e.g. "Mar 25" */
export const formatShortDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

/** Formats a date string to time — e.g. "02:30 PM" */
export const formatTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

/**
 * Formats the time remaining until `expiresAt` (Unix epoch SECONDS) relative to
 * `now` (epoch ms), with hours/minutes/seconds granularity. Leading zero units
 * are dropped, but seconds are always shown (it's the ticking unit). Returns
 * null when the remaining time is <= 0 (expired) or the value is invalid, so
 * callers can render an expired state.
 *
 * Examples: "2 hours 15 minutes 30 seconds", "15 minutes 3 seconds",
 * "42 seconds".
 */
export const formatTimeRemaining = (expiresAt: number, now: number): string | null => {
  if (!Number.isFinite(expiresAt)) return null;
  const endMs = expiresAt * MS_PER_SECOND; // epoch seconds -> ms
  const diffMs = endMs - now;
  if (diffMs <= 0) return null;
  const totalSeconds = Math.floor(diffMs / MS_PER_SECOND);
  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  }
  if (minutes > 0 || hours > 0) {
    parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
  }
  parts.push(`${seconds} ${seconds === 1 ? "second" : "seconds"}`);
  return parts.join(" ");
};
